import type {
  AbstractControl,
  UntypedFormArray,
  UntypedFormGroup,
} from '@angular/forms';
import { isFormArray, isFormGroup } from './forms.type-guards';
import {
  ControlMapFn,
  ControlPath,
  ControlPredicateFn,
} from './angular-forms.types';

/**
 * Creates a function that will filter, then map controls within
 * a `FormGroup`.
 *
 * @param predicate
 * Function than is called for each nested control.
 *
 * @param map
 * Function that is called for each nested control that passes the
 * given `predicate`. The value returned by this function will be added
 * to the result for the key for the nested control.
 */
function filterMapFormGroup<T>(
  predicate: ControlPredicateFn,
  map: ControlMapFn<T>,
) {
  /**
   * @param parent
   * FormGroup for which the nested controls should be filtered and mapped.
   *
   * @param path
   * A path to be passed with the parent.
   */
  return (parent: UntypedFormGroup, path: ControlPath): Record<string, T> =>
    Object.entries(parent.controls).reduce(
      (acc, [key, childCtrl]) => {
        const childPath = [...path, key];
        if (predicate(childCtrl, childPath)) {
          acc[key] = map(childCtrl, childPath);
        }
        return acc;
      },
      {} as Record<string, T>,
    );
}

/**
 * Creates a function that will filter, then map controls within
 * a `FormArray`.
 *
 * @param predicate
 * Function than is called for each nested control.
 *
 * @param map
 * Function that is called for each nested control that passes the
 * given `predicate`. The value returned by this function will be added
 * to the result for the key for the nested control.
 */
function filterMapFormArray<T>(
  predicate: ControlPredicateFn,
  map: ControlMapFn<T>,
) {
  /**
   * @param parent
   * FormArray for which the nested controls should be filtered and mapped.
   *
   * @param path
   * A path to be passed with the parent.
   */
  return (parent: UntypedFormArray, path: ControlPath): T[] =>
    parent.controls.reduce((acc, childCtrl, index) => {
      const childPath = [...path, index];
      if (predicate(childCtrl, childPath)) {
        acc.push(map(childCtrl, childPath));
      }
      return acc;
    }, [] as T[]);
}

/**
 * Creates a recursive traversal function that will filter and map
 * nested controls.
 *
 * @param predicate
 * Function than is called for each (nested) control.
 *
 * @param map
 * Function that is called for each (nested) control that passes the
 * given `predicate`. The value returned by this function will be added
 * to the result for the control.
 *
 * @returns
 * A function that when called with a control will recursively traverse
 * nested controls by first filtering them based on the given `predicate`
 * and if fulfilled will be passed to the given `map` function.
 */
function makeRecursiveTraverserWith<T>(
  predicate: ControlPredicateFn,
  map: ControlMapFn<T>,
) {
  const transformFormArray = filterMapFormArray(predicate, (ctrl, path) =>
    defaultTraverser(ctrl, path),
  );
  const transformFormGroup = filterMapFormGroup(predicate, (ctrl, path) =>
    defaultTraverser(ctrl, path),
  );

  const defaultTraverser: ControlMapFn = (ctrl, path) =>
    isFormGroup(ctrl)
      ? transformFormGroup(ctrl, path)
      : isFormArray(ctrl)
        ? transformFormArray(ctrl, path)
        : map(ctrl, path);

  return (root: AbstractControl) => defaultTraverser(root, []);
}

/**
 *
 * @param predicate
 * Predicate function that is executed for each (nested) control.
 * It is also called for `FormGroup`s and `FormArray`s.
 *
 * @returns
 * Function that when called with a control will traverse nested
 * controls and filter based on the given `predicate`.
 */
export function filterFormControlValue(
  predicate: ControlPredicateFn,
): (root: AbstractControl) => unknown | undefined {
  const getValueFromFiltered = makeRecursiveTraverserWith(
    predicate,
    (ctrl) => ctrl.value as unknown,
  );

  /**
   * @param root
   * Root form control for which the value should be filtered.
   *
   * @returns
   * The aggregated value for the control.
   */
  return (root) =>
    predicate(root, []) ? getValueFromFiltered(root) : undefined;
}
